/* SpawnAnything — demo economy rules (diamonds only, no real money). Pure functions over an inventory
   (src/game/inventory.js); all numbers live in ECONOMY so they can be tuned (or served) in one place. */

export const ECONOMY = {
  cratePrice: 100,
  supplyDropChance: 0.125,
  daily: 50, // once per calendar day
  battle: {
    win: 20, loss: 8, // base reward (draw counts as loss)
    enemyMax: 30, // + floor(sqrt(enemies)), capped: 1 enemy = +1, 100 = +10, 900+ = +30
    secondsPer: 20, timeMax: 15, // + 1 per 20 s of battle, capped
    lossShare: 0.5, // a loss only gets this share of the bonuses
    shortSeconds: 10, shortFactor: 0.25, // over in under 10 s: a quarter
    cap: 60, // per battle
    dailyCap: 400, // from battles per calendar day
  },
  // one-time milestones (02.10.2026: free sandbox battles no longer pay - they were farmable): every NEW campaign star,
  // every wave beyond the own best in the defence mode
  milestone: { campaignStar: 10, campaignBonus: 10, waveRecord: 5, daily: 5 }, // daily: the first finished daily battle of a day
  // first time an item drops (on top of the unlock) / later duplicates
  firstUnlock: { selten: 5, episch: 10, legendaer: 25, mythisch: 50, exotisch: 100 },
  duplicate: { selten: 10, episch: 20, legendaer: 40, mythisch: 80, exotisch: 150 },
  // AI figures (text, photo, building): library and already generated (cached) figures are free; a NEW model generation
  // costs `cost` diamonds after `freeOnce` free ones per account (one-time, not per day)
  generation: { cost: 3, freeOnce: 3 },
  // diamond packs (preview only - no payment code yet; prices incl. VAT)
  packs: [
    { id: 'p100', diamonds: 100, price: '0,99 €', figures: 33 },
    { id: 'p550', diamonds: 550, price: '4,99 €', figures: 183, bonus: '10 % Bonus' },
    { id: 'p1200', diamonds: 1200, price: '9,99 €', figures: 400, bonus: '20 % Bonus' },
  ],
};

/** { freeLeft, cost, canPay } for new AI figures. The free ones are counted once per account (earned.genFreeUsed). */
export function generationQuota(inv, E = ECONOMY.generation) {
  const used = Math.max(0, (inv.meta().earned || {}).genFreeUsed | 0);
  return { freeLeft: Math.max(0, E.freeOnce - used), cost: E.cost, canPay: inv.canAfford(E.cost) };
}
/** May the player request a NEW model generation? (library/cached figures are always allowed) */
export const canGenerate = (inv, E = ECONOMY.generation) => { const q = generationQuota(inv, E); return q.freeLeft > 0 || q.canPay; };
/** Pay for one new model generation: a free one first, else `cost` diamonds. Returns { free, cost } or null (cannot pay). */
export function chargeGeneration(inv, E = ECONOMY.generation) {
  const q = generationQuota(inv, E);
  if (q.freeLeft > 0) { inv.update(m => { m.earned.genFreeUsed = (m.earned.genFreeUsed | 0) + 1; }); return { free: true, cost: 0, freeLeft: q.freeLeft - 1 }; }
  return inv.spend(E.cost, 'figur') ? { free: false, cost: E.cost, freeLeft: 0 } : null;
}

/** Local calendar day "YYYY-MM-DD" (daily bonus + battle cap reset at local midnight). */
export function dayKey(t = Date.now()) {
  const d = new Date(t);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Diamonds for one finished battle before the daily cap. won: true | false (draw = false). */
export function battleReward({ won, enemies = 0, seconds = 0 }, E = ECONOMY.battle) {
  const enemyB = Math.min(E.enemyMax, Math.floor(Math.sqrt(Math.max(0, enemies))));
  const timeB = Math.min(E.timeMax, Math.floor(Math.max(0, seconds) / E.secondsPer));
  let n = won ? E.win + enemyB + timeB : E.loss + Math.floor((enemyB + timeB) * E.lossShare);
  if (seconds < E.shortSeconds) n = Math.floor(n * E.shortFactor);
  return Math.max(0, Math.min(E.cap, n));
}

/** Count a finished sandbox / multiplayer battle (statistics only, no diamonds). */
export function countBattle(inv, won) {
  inv.update(m => { m.stats.battles++; if (won) m.stats.wins++; });
}
/** One-time milestone reward (ECONOMY.milestone[kind] per step, e.g. per new campaign star); returns the amount. */
export function awardMilestone(inv, kind, steps) {
  const amount = Math.max(0, steps | 0) * (ECONOMY.milestone[kind] || 0);
  if (amount) inv.grant({ kind: 'diamonds', amount, reason: kind });
  return amount;
}

/** Book a finished battle: grants diamonds (respecting the daily cap) and counts stats. */
export function awardBattle(inv, { won, enemies, seconds }, t = Date.now()) {
  const day = dayKey(t), raw = battleReward({ won, enemies, seconds });
  let amount = 0, capped = false;
  inv.batch(() => {
    inv.update(m => {
      if (m.earned.day !== day) { m.earned.day = day; m.earned.battles = 0; }
      amount = Math.max(0, Math.min(raw, ECONOMY.battle.dailyCap - m.earned.battles));
      capped = amount < raw;
      m.earned.battles += amount; m.stats.battles++; if (won) m.stats.wins++;
    });
    if (amount) inv.grant({ kind: 'diamonds', amount, reason: won ? 'sieg' : 'schlacht' });
  });
  return { amount, raw, capped };
}

/** Daily login bonus: once per calendar day. Returns the granted amount (0 if already claimed today). */
export function claimDaily(inv, t = Date.now()) {
  const day = dayKey(t);
  if (inv.meta().daily.last === day) return 0;
  const prev = dayKey(t - 86400000);
  inv.batch(() => {
    inv.update(m => { m.daily.streak = m.daily.last === prev ? m.daily.streak + 1 : 1; m.daily.last = day; });
    inv.grant({ kind: 'diamonds', amount: ECONOMY.daily, reason: 'tagesbonus' });
  });
  return ECONOMY.daily;
}

/** Pay for a crate and receive the rolled item in ONE transaction (+ first-unlock bonus or duplicate refund).
    Returns null when the balance is too low (nothing changes). */
export function openCrate(inv, item, price = ECONOMY.cratePrice) {
  if (!inv.canAfford(price)) return null;
  return inv.batch(() => {
    inv.spend(price, 'kiste');
    const { isNew, count } = inv.grant({ kind: item.kind, key: item.key, rarity: item.rarity, reason: 'kiste' });
    const bonus = (isNew ? ECONOMY.firstUnlock : ECONOMY.duplicate)[item.rarity] || 0;
    if (bonus) inv.grant({ kind: 'diamonds', amount: bonus, reason: isNew ? 'erstfund' : 'duplikat' });
    inv.update(m => { m.stats.crates++; });
    return { isNew, count, bonus };
  });
}
